import type { ReactNode } from 'react';
import { Montant, Nombre } from '../../../composants/ui/index.ts';
import { dateLongue } from '../outils.ts';

/**
 * Le chiffre du jour, sous forme de TICKET — RG-143.
 *
 * Le document que ce commerçant lit déjà tous les jours, c'est le reçu que
 * l'appareil imprime au comptoir : même police à chasse fixe, mêmes
 * pointillés, même bord déchiré. Plutôt qu'un bandeau générique, le tableau
 * de bord s'ouvre sur ce document-là.
 *
 * La marge n'apparaît que si le serveur l'a envoyée (`rapport.marge.lire`) :
 * c'est la MARGE BRUTE (CA moins coût des marchandises, RG-070), jamais un
 * bénéfice — ni loyer ni salaires n'en sont déduits.
 */
export function TicketDuJour({
  boutiqueLibelle,
  date,
  ventesNombre,
  ventesTotal,
  panierMoyen,
  marge,
}: {
  boutiqueLibelle: string;
  date: string;
  ventesNombre: number;
  ventesTotal: number;
  panierMoyen: number;
  marge?: { margeBrute: number; tauxMargePct: number } | undefined;
}): ReactNode {
  const heure = new Date().toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Africa/Porto-Novo',
  });

  // Empreinte purement décorative, dérivée du total : elle ne prétend
  // encoder aucune donnée, seulement rappeler le code-barres d'un vrai
  // ticket. Déterministe pour ne pas sauter à chaque re-rendu.
  const barres = Array.from({ length: 14 }, (_, index) =>
    8 + ((ventesTotal + index * 37) % 13),
  );

  return (
    <div className="tdb-ticket-cadre">
      <div className="tdb-ticket" aria-label="Relevé du jour">
        <div className="tdb-ticket__entete">
          <div className="tdb-ticket__boutique">{boutiqueLibelle}</div>
          <div className="tdb-ticket__date mono">
            {dateLongue(date)} · relevé {heure}
          </div>
        </div>
        <div className="tdb-ticket__pointilles" />
        <div className="tdb-ticket__ligne">
          <span>Ventes</span>
          <span className="mono">
            <Nombre valeur={ventesNombre} />
          </span>
        </div>
        <div className="tdb-ticket__ligne">
          <span>Panier moyen</span>
          <span className="mono">
            <Montant valeur={panierMoyen} />
          </span>
        </div>
        {marge !== undefined && (
          <div className="tdb-ticket__ligne">
            <span>Marge brute</span>
            <span className="mono tdb-ticket__marge">
              <Montant valeur={marge.margeBrute} /> · {marge.tauxMargePct.toFixed(1)} %
            </span>
          </div>
        )}
        <div className="tdb-ticket__pointilles" />
        <div className="tdb-ticket__total">
          <span>Chiffre d’affaires</span>
          <span className="tdb-ticket__valeur mono">
            <Montant valeur={ventesTotal} />
          </span>
        </div>
        <div className="tdb-ticket__pied">
          Généré automatiquement — ne remplace pas le rapport Z de caisse
        </div>
        <div className="tdb-ticket__code" aria-hidden="true">
          {barres.map((hauteur, index) => (
            <span key={index} style={{ height: `${String(hauteur)}px` }} />
          ))}
        </div>
      </div>
    </div>
  );
}
